import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useState } from "react";
import type { SendingDomainResponse } from "@common/schemas/sending-domain.schema";
import {
    SendingDomainMethod,
    SendingDomainStatus,
} from "@common/types/domains/emailSending";
import {
    useDeleteSendingDomain,
    useVerifySendingDomain,
} from "@api/sendingDomain.api";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { DnsRecordsModal } from "../DnsRecordsModal";
import { SuggestedConnectButton } from "../SuggestedConnectButton";
import {
    isSuggestedSendingDomainRow,
    type SendingDomainRow,
    type SendingDomainsTableMeta,
    type SuggestedSendingDomainRow,
} from "../sendingDomainsViewConfig";

const SuggestedDomainActions = memo(
    ({
        row,
        onConnectDomain,
    }: {
        row: SuggestedSendingDomainRow;
        onConnectDomain?: (domain: string) => void;
    }) => (
        <SuggestedConnectButton
            loading={false}
            onClick={() => onConnectDomain?.(row.domain)}
        />
    ),
);

const ConnectedDomainActions = memo(
    ({ domain }: { domain: SendingDomainResponse }) => {
        const [showDnsRecords, setShowDnsRecords] = useState(false);
        const [showDeleteConfirmation, setShowDeleteConfirmation] =
            useState(false);
        const verifyDomain = useVerifySendingDomain();
        const deleteDomain = useDeleteSendingDomain();
        const { toast } = useToaster();

        const handleVerify = useCallback(async () => {
            try {
                const updated = await verifyDomain.mutateAsync(domain.id);
                if (updated.status === SendingDomainStatus.verified) {
                    toast("Domain verified");
                }
            } catch {
                // The API client already toasts the error body.
            }
        }, [domain.id, toast, verifyDomain]);

        const confirmDelete = useCallback(async () => {
            try {
                await deleteDomain.mutateAsync(domain.id);
                setShowDeleteConfirmation(false);
                toast("Domain removed");
            } catch {
                // The API client already toasts the error body.
            }
        }, [deleteDomain, domain.id, toast]);

        const isDns = domain.method === SendingDomainMethod.dns;
        const isVerified = domain.status === SendingDomainStatus.verified;

        return (
            <ViewTableCell captureClicks>
                <Menu.More>
                    {isDns && (
                        <Menu.Item onClick={() => setShowDnsRecords(true)}>
                            View DNS records
                        </Menu.Item>
                    )}
                    {!isVerified && (
                        <Menu.Item
                            onClick={() => {
                                void handleVerify();
                            }}
                        >
                            Verify
                        </Menu.Item>
                    )}
                    <Menu.Item
                        destructive
                        onClick={() => setShowDeleteConfirmation(true)}
                    >
                        Remove domain
                    </Menu.Item>
                </Menu.More>

                {isDns && (
                    <DnsRecordsModal
                        domain={showDnsRecords ? domain : null}
                        onClose={() => setShowDnsRecords(false)}
                    />
                )}

                <ConfirmationModal
                    show={showDeleteConfirmation}
                    onHide={() => setShowDeleteConfirmation(false)}
                    closeButton
                    title="Remove sending domain?"
                    question={`Are you sure you want to remove "${domain.domain}"? From addresses on this domain will be deleted.`}
                    onConfirm={() => {
                        void confirmDelete();
                    }}
                    onReject={() => setShowDeleteConfirmation(false)}
                    yes="Remove"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />
            </ViewTableCell>
        );
    },
);

export const SendingDomainActionsCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, SendingDomainRow, string>) => {
        const domain = row.original;
        if (isSuggestedSendingDomainRow(domain)) {
            const meta = table.options.meta as
                | SendingDomainsTableMeta
                | undefined;
            return (
                <SuggestedDomainActions
                    row={domain}
                    onConnectDomain={meta?.callbacks?.onConnectDomain}
                />
            );
        }
        return <ConnectedDomainActions domain={domain} />;
    },
);
